import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface PorridgeIllustrationProps {
    className?: string
}

// Top-down bowl of chicken porridge, drawn on a 560 x 560 canvas with the bowl centered at (280, 304).

const chickenShreds = [
    { x: 206, y: 276, rx: 24, ry: 8, angle: -20, fiber: true },
    { x: 232, y: 286, rx: 22, ry: 7, angle: 15, fiber: true },
    { x: 200, y: 300, rx: 20, ry: 7, angle: 35, fiber: false },
    { x: 228, y: 311, rx: 26, ry: 8, angle: -10, fiber: true },
    { x: 247, y: 268, rx: 18, ry: 6, angle: -40, fiber: false },
    { x: 186, y: 286, rx: 16, ry: 6, angle: 70, fiber: false },
    { x: 214, y: 322, rx: 18, ry: 6, angle: 20, fiber: true }
]

const gingerSticks = [
    { x: 296, y: 306, angle: -30 },
    { x: 308, y: 314, angle: 15 },
    { x: 290, y: 320, angle: 60 },
    { x: 312, y: 326, angle: -55 },
    { x: 300, y: 332, angle: 5 },
    { x: 284, y: 312, angle: -75 },
    { x: 318, y: 304, angle: 40 }
]

const scallionRings = [
    { x: 262, y: 214, r: 6.5 },
    { x: 296, y: 232, r: 7 },
    { x: 232, y: 190, r: 6 },
    { x: 318, y: 272, r: 6.5 },
    { x: 378, y: 296, r: 7 },
    { x: 404, y: 262, r: 6 },
    { x: 270, y: 262, r: 5.5 },
    { x: 322, y: 356, r: 6.5 },
    { x: 178, y: 334, r: 6 },
    { x: 262, y: 428, r: 7 },
    { x: 302, y: 444, r: 6 },
    { x: 150, y: 300, r: 6.5 }
]

const shallots = [
    { x: 248, y: 248, angle: 20 },
    { x: 282, y: 284, angle: -40 },
    { x: 196, y: 258, angle: 60 },
    { x: 258, y: 342, angle: 10 },
    { x: 334, y: 294, angle: 80 },
    { x: 360, y: 324, angle: -30 },
    { x: 300, y: 206, angle: 45 },
    { x: 214, y: 214, angle: -10 },
    { x: 180, y: 362, angle: 30 },
    { x: 286, y: 404, angle: -60 },
    { x: 346, y: 198, angle: 30 },
    { x: 396, y: 346, angle: 15 },
    { x: 240, y: 446, angle: -20 },
    { x: 160, y: 326, angle: 50 }
]

const sesameDrops = [
    { x: 300, y: 262, r: 9 },
    { x: 366, y: 300, r: 6 },
    { x: 196, y: 346, r: 7 },
    { x: 318, y: 410, r: 6 },
    { x: 256, y: 236, r: 5 },
    { x: 400, y: 322, r: 5 }
]

const pepperSpecks = [
    [230, 250], [244, 226], [270, 240], [300, 290], [326, 282], [356, 312],
    [372, 340], [340, 336], [316, 384], [290, 358], [262, 320], [238, 350],
    [206, 334], [184, 312], [172, 276], [214, 244], [256, 206], [318, 224],
    [392, 286], [410, 300], [284, 436], [320, 436], [250, 412], [352, 370]
]

const cilantroSprigs = [
    { x: 392, y: 214, angle: 0 },
    { x: 176, y: 250, angle: 40 },
    { x: 300, y: 420, angle: -20 }
]

const swirls = [
    'M196 236c18-22 46-32 76-30',
    'M352 196c26 6 46 22 56 44',
    'M150 318c2 30 14 56 34 76',
    'M404 360c-8 26-26 50-50 64',
    'M250 452c20 6 42 6 62 0'
]

const steam = [
    { d: 'M236 150c-14-14-14-28 0-42s14-28 0-42', delay: '0s' },
    { d: 'M280 138c-16-16-16-32 0-48s16-32 0-48', delay: '1.2s' },
    { d: 'M324 150c-14-14-14-28 0-42s14-28 0-42', delay: '2.4s' }
]

const leaf = 'M0 0c4-7 13-7 15 0c-2 7-11 7-15 0z'
const spoonHead = 'M0 0C0-22 18-28 40-27C62-26 80-16 80 0C80 16 62 26 40 27C18 28 0 22 0 0Z'
const spoonHandle = 'M74-8L196-6C202-6 206-3 206 0C206 3 202 6 196 6L74 8Z'

export const PorridgeIllustration: FC<PorridgeIllustrationProps> = ({ className }): ReactNode => {
    return (
        <svg
            viewBox='0 0 560 560'
            role='img'
            aria-label="Illustration of a bowl of chicken porridge topped with pulled chicken, ginger, scallions, a soft egg, crispy shallots and fried dough sticks"
            className={cn('h-auto w-full', className)}
        >
            <defs>
                <radialGradient id='gp-rim' cx='40%' cy='34%' r='70%'>
                    <stop offset='0%' stopColor='#FFFDF8' />
                    <stop offset='100%' stopColor='#EFE1C6' />
                </radialGradient>

                <radialGradient id='gp-porridge' cx='56%' cy='60%' r='62%'>
                    <stop offset='0%' stopColor='#FCF5E6' />
                    <stop offset='70%' stopColor='#F4E4C6' />
                    <stop offset='100%' stopColor='#E7D0A6' />
                </radialGradient>

                <linearGradient id='gp-wall-shadow' x1='0' y1='0' x2='1' y2='1'>
                    <stop offset='0%' stopColor='#A8824C' stopOpacity='0.32' />
                    <stop offset='42%' stopColor='#A8824C' stopOpacity='0' />
                </linearGradient>
            </defs>

            <g fill='none' stroke='#FFFFFF' strokeOpacity='0.14' strokeWidth='1.5'>
                <circle cx='280' cy='304' r='238' />
                <circle cx='280' cy='304' r='266' />
            </g>

            <circle cx='296' cy='324' r='198' fill='#3A1704' fillOpacity='0.24' />
            <circle cx='280' cy='304' r='196' fill='url(#gp-rim)' />
            <circle cx='280' cy='304' r='189' fill='none' stroke='#4E7A2C' strokeWidth='4' />
            <circle cx='280' cy='304' r='183' fill='none' stroke='#4E7A2C' strokeWidth='1.5' />
            <path d='M98.2 237.8A193.5 193.5 0 0 1 213.8 122.2' fill='none' stroke='#FFFFFF' strokeOpacity='0.9' strokeWidth='2.5' strokeLinecap='round' />
            <circle cx='280' cy='304' r='176' fill='#EFE0C3' />
            <circle cx='280' cy='304' r='162' fill='url(#gp-porridge)' />
            <circle cx='280' cy='304' r='162' fill='url(#gp-wall-shadow)' />

            <g fill='none' stroke='#E2CA9C' strokeWidth='2.5' strokeLinecap='round' strokeOpacity='0.85'>
                {swirls.map(d => (
                    <path key={d} d={d} />
                ))}
            </g>

            <g fill='#D99A2E' fillOpacity='0.3'>
                {sesameDrops.map(drop => (
                    <circle key={`${drop.x}-${drop.y}`} cx={drop.x} cy={drop.y} r={drop.r} />
                ))}
            </g>

            <g>
                {chickenShreds.map(shred => (
                    <g key={`${shred.x}-${shred.y}`} transform={`translate(${shred.x} ${shred.y}) rotate(${shred.angle})`}>
                        <ellipse rx={shred.rx} ry={shred.ry} fill='#EECF9E' stroke='#D3A666' strokeWidth='1.5' />
                        {shred.fiber && (
                            <path d={`M${-shred.rx + 8} 0h${shred.rx * 2 - 16}`} stroke='#DDB57C' strokeWidth='1.2' strokeLinecap='round' />
                        )}
                    </g>
                ))}
            </g>

            <g fill='#F4D47C' stroke='#DDB04E' strokeWidth='1'>
                {gingerSticks.map(stick => (
                    <rect
                        key={`${stick.x}-${stick.y}`}
                        x='-16'
                        y='-2.5'
                        width='32'
                        height='5'
                        rx='2.5'
                        transform={`translate(${stick.x} ${stick.y}) rotate(${stick.angle})`}
                    />
                ))}
            </g>

            <g transform='translate(342 246) rotate(-24)'>
                <ellipse cx='3' cy='5' rx='44' ry='34' fill='#B8925A' fillOpacity='0.25' />
                <ellipse rx='44' ry='34' fill='#FFFDF7' stroke='#EBDCC2' strokeWidth='1.5' />
                <circle cx='4' cy='2' r='20' fill='#F5A63A' />
                <circle cx='5' cy='3' r='12' fill='#E98A20' />
                <ellipse cx='-3' cy='-6' rx='6' ry='3.5' fill='#FFFFFF' fillOpacity='0.6' />
            </g>

            <g transform='translate(234 384) rotate(-28)'>
                <g fill='#8A5A20' fillOpacity='0.2'>
                    <rect x='-48' y='-19' width='104' height='22' rx='11' />
                    <rect x='-48' y='7' width='104' height='22' rx='11' />
                </g>
                <g fill='#D8913A' stroke='#B56F22' strokeWidth='2'>
                    <rect x='-52' y='-24' width='104' height='22' rx='11' />
                    <rect x='-52' y='2' width='104' height='22' rx='11' />
                </g>
                <path d='M-40 -13h80M-40 13h80' stroke='#F0BC6A' strokeWidth='3' strokeLinecap='round' strokeOpacity='0.75' />
                <g fill='#B56F22' fillOpacity='0.5'>
                    <circle cx='-30' cy='-18' r='1.5' />
                    <circle cx='-5' cy='-8' r='1.5' />
                    <circle cx='22' cy='-17' r='1.5' />
                    <circle cx='-18' cy='8' r='1.5' />
                    <circle cx='10' cy='18' r='1.5' />
                    <circle cx='34' cy='8' r='1.5' />
                </g>
            </g>

            <g fill='#A8CD76' stroke='#5B8C31' strokeWidth='3'>
                {scallionRings.map(ring => (
                    <circle key={`${ring.x}-${ring.y}`} cx={ring.x} cy={ring.y} r={ring.r} />
                ))}
            </g>

            <g fill='none' strokeWidth='3.5' strokeLinecap='round'>
                {shallots.map((piece, index) => (
                    <path
                        key={`${piece.x}-${piece.y}`}
                        d='M-6 2q5-8 12-1'
                        stroke={index % 2 === 0 ? '#A65A22' : '#8C4A1A'}
                        transform={`translate(${piece.x} ${piece.y}) rotate(${piece.angle})`}
                    />
                ))}
            </g>

            <g fill='#4F8A3A' stroke='#3E7030' strokeWidth='1'>
                {cilantroSprigs.map(sprig => (
                    <g key={`${sprig.x}-${sprig.y}`} transform={`translate(${sprig.x} ${sprig.y}) rotate(${sprig.angle})`}>
                        <path d={leaf} transform='rotate(-90)' />
                        <path d={leaf} transform='rotate(30)' />
                        <path d={leaf} transform='rotate(150)' />
                    </g>
                ))}
            </g>

            <g fill='#6B5440' fillOpacity='0.5'>
                {pepperSpecks.map(([x, y]) => (
                    <circle key={`${x}-${y}`} cx={x} cy={y} r='1.5' />
                ))}
            </g>

            <g transform='translate(337 381) rotate(42)' fill='#5A3A1C' fillOpacity='0.22'>
                <path d={spoonHandle} />
                <path d={spoonHead} />
            </g>

            <g transform='translate(330 372) rotate(42)'>
                <path d={spoonHandle} fill='#FFFDF8' stroke='#E3D3B5' strokeWidth='1.5' />
                <path d='M90-3L190-2' stroke='#FFFFFF' strokeWidth='2' strokeLinecap='round' />
                <path d={spoonHead} fill='#FFFDF8' stroke='#E3D3B5' strokeWidth='1.5' />
                <path d='M10 0C10-16 24-20 40-19C58-18 70-11 70 0C70 11 58 18 40 19C24 20 10 16 10 0Z' fill='#F4ECDD' />
                <path d='M18 2C18-9 28-12 40-11C52-10 60-5 60 2C60 9 52 12 40 12C28 12 18 10 18 2Z' fill='#F3E3C4' />
                <circle cx='46' cy='0' r='4' fill='#A8CD76' stroke='#5B8C31' strokeWidth='2' />
            </g>

            <g fill='none' stroke='#FFFFFF' strokeWidth='7' strokeLinecap='round' strokeOpacity='0.8'>
                {steam.map(wisp => (
                    <path
                        key={wisp.d}
                        d={wisp.d}
                        className='animate-steam'
                        style={{ animationDelay: wisp.delay }}
                    />
                ))}
            </g>
        </svg>
    )
}
